/**
 * flowModel — pure helpers for the AI Flows editor.
 *
 * The wire spec uses OBJECTS keyed by metric (hard_constraints, scoring_weights)
 * because that's what the backend serializes to YAML and what the runtime tools
 * read. The form is easier to edit as ARRAYS of rows, so these helpers convert
 * between the two and do light client-side validation (the backend is the
 * authoritative validator). No YAML here — the backend owns serde.
 */

let _rowSeq = 0
const nextId = () => `r${++_rowSeq}`

/** All 8 report section ids — must match backend report.ALL_SECTIONS in order. */
export const REPORT_SECTIONS = [
  'exec_summary',
  'application_profile',
  'ranked_shortlist',
  'ragone',
  'provenance',
  'per_cell_rationale',
  'pack_sizing',
  'next_steps',
]

/** The 5 core sections shown by default when no spec.report is loaded. */
export const DEFAULT_REPORT_SECTIONS = [
  'exec_summary',
  'application_profile',
  'ranked_shortlist',
  'ragone',
  'provenance',
]

export function emptySpec() {
  return {
    branch_label: '',
    hard_constraints: {},
    scoring_weights: {},
    minimize: [],
    must_ask_questions: [],
    cross_cutting_questions: [],
    defaults: {},
    plot_x: '',
    plot_y: '',
    disclaimers: [],
  }
}

/** Detect a ConstraintSpec's shape: 'list' (allow-list), 'exclude' (deny-list,
 *  v2 `{not_in: [...]}`), 'cmp' (op+value), or 'range' (min/max). */
export function constraintKind(spec) {
  if (Array.isArray(spec)) return 'list'
  if (spec && typeof spec === 'object') {
    if ('not_in' in spec) return 'exclude'
    if ('op' in spec || 'value' in spec) return 'cmp'
    return 'range'
  }
  return 'cmp'
}

/** hard_constraints object → editor rows. */
export function constraintsToRows(hard) {
  const out = []
  for (const [metric, spec] of Object.entries(hard || {})) {
    const kind = constraintKind(spec)
    const row = {
      id: nextId(),
      metric,
      kind,
      op: '>=',
      value: '',
      unit: '',
      values: [],
      min: '',
      max: '',
    }
    if (kind === 'list') {
      row.values = spec.map((v) => String(v))
    } else if (kind === 'exclude') {
      row.values = (spec.not_in || []).map((v) => String(v))
    } else if (kind === 'cmp') {
      row.op = spec.op || '>='
      row.value = spec.value ?? ''
      row.unit = spec.unit || ''
    } else {
      row.min = spec.min ?? ''
      row.max = spec.max ?? ''
      row.unit = spec.unit || ''
    }
    out.push(row)
  }
  return out
}

const _num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** editor rows → hard_constraints object (drops rows without a metric). */
export function rowsToConstraints(rows) {
  const out = {}
  for (const row of rows || []) {
    if (!row.metric) continue
    if (row.kind === 'list') {
      const vals = (row.values || []).map((v) => String(v).trim()).filter(Boolean)
      out[row.metric] = vals
    } else if (row.kind === 'exclude') {
      const vals = (row.values || []).map((v) => String(v).trim()).filter(Boolean)
      out[row.metric] = { not_in: vals }
    } else if (row.kind === 'range') {
      const c = {}
      if (_num(row.min) !== null) c.min = _num(row.min)
      if (_num(row.max) !== null) c.max = _num(row.max)
      if (row.unit) c.unit = row.unit
      out[row.metric] = c
    } else {
      const c = { op: row.op || '>=' }
      if (_num(row.value) !== null) c.value = _num(row.value)
      if (row.unit) c.unit = row.unit
      out[row.metric] = c
    }
  }
  return out
}

/** scoring_weights + minimize → editor rows. */
export function weightsToRows(weights, minimize) {
  const min = new Set(minimize || [])
  return Object.entries(weights || {}).map(([metric, weight]) => ({
    id: nextId(),
    metric,
    weight: typeof weight === 'number' ? weight : Number(weight) || 0,
    minimize: min.has(metric),
  }))
}

/** editor rows → { scoring_weights, minimize }. */
export function rowsToWeights(rows) {
  const scoring_weights = {}
  const minimize = []
  for (const row of rows || []) {
    if (!row.metric) continue
    scoring_weights[row.metric] = _num(row.weight) ?? 0
    if (row.minimize) minimize.push(row.metric)
  }
  return { scoring_weights, minimize }
}

export function weightSum(rows) {
  return (rows || []).reduce((s, r) => s + (_num(r.weight) ?? 0), 0)
}

/** Scale weight rows so they sum to 1.0 (no-op when the sum is 0). */
export function normalizeWeightRows(rows) {
  const sum = weightSum(rows)
  if (!sum) return rows
  return rows.map((r) => ({ ...r, weight: Math.round(((_num(r.weight) ?? 0) / sum) * 100) / 100 }))
}

/** Question `phase` in the guided flow (v2). Absent on the wire = 'reduction'. */
export const QUESTION_PHASES = ['reduction', 'requirement', 'weighting']

/** Question `input` widget type (v2). Absent on the wire = 'choice'. */
export const INPUT_TYPES = ['choice', 'number', 'topology']

/**
 * Pack-level ranking quantities — mirrors backend pack_math.PACK_SCORING_FIELDS.
 *
 * These are NOT cell columns: the backend derives them per candidate from the
 * sized pack, so they never appear in the metric catalogue the editor loads.
 * Without this list the client validator would reject every flow that ranks the
 * way the MATLAB reference does (pack mass, pack range, runtime) as "not an
 * AI-visible metric".
 */
export const PACK_SCORING_FIELDS = [
  'pack_energy_wh',
  'pack_peak_power_w',
  'pack_cont_power_w',
  'pack_capacity_ah',
  'pack_mass_kg',
  'pack_volume_l',
  'pack_range_km',
  'pack_runtime_min',
]

/** Score normalisation — mirrors backend selection_flows._VALID_NORMALIZATIONS. */
export const NORMALIZATIONS = ['zscore', 'minmax']

const isPackField = (key) => PACK_SCORING_FIELDS.includes(key)
/** A metric key the flow may name: a catalogue column or a pack quantity. */
const isKnownMetric = (known, key) => isPackField(key) || !known || known.has(key)

/** Requirement fields a `system_requirements_map` question may target — mirrors
 *  backend SYSTEM_REQUIREMENT_FIELDS (selection_flows.py). */
export const SYSTEM_REQUIREMENT_FIELDS = [
  'pack_voltage_v',
  'pack_peak_power_w',
  'pack_cont_power_w',
  'pack_energy_wh',
  'range_km',
  'energy_per_distance_wh_km',
  'max_weight_kg',
  'max_volume_l',
  'topology',
  'parallel_count',
]

export function newQuestion() {
  return {
    id: nextId(),
    slug: '',
    question: '',
    options: [],
    allow_freeform: true,
    multi_select: false,
    phase: 'reduction',
    inputType: 'choice',
    inputUnit: '',
    inputMin: '',
    inputMax: '',
  }
}

export function newOption() {
  return { id: nextId(), label: '', value: '' }
}

export function questionsToRows(questions) {
  return (questions || []).map((q) => ({
    id: nextId(),
    slug: q.slug || '',
    question: q.question || '',
    options: (q.options || []).map((o) => ({ id: nextId(), label: o.label || '', value: o.value || '' })),
    allow_freeform: q.allow_freeform !== false,
    multi_select: !!q.multi_select,
    phase: q.phase || 'reduction',
    inputType: q.input?.type || 'choice',
    inputUnit: q.input?.unit || '',
    inputMin: q.input?.min ?? '',
    inputMax: q.input?.max ?? '',
  }))
}

/** editor rows → must_ask_questions/cross_cutting_questions array. `phase` and
 *  `input` are emitted ONLY when they diverge from the v1 default (reduction /
 *  choice), so a flow with no v2 fields serializes byte-identically to before. */
export function rowsToQuestions(rows) {
  return (rows || [])
    .filter((q) => q.question && q.question.trim())
    .map((q) => {
      const out = {
        slug: (q.slug || '').trim(),
        question: q.question.trim(),
        options: (q.options || [])
          .filter((o) => o.label && o.label.trim())
          .map((o) => ({ label: o.label.trim(), value: (o.value || o.label).trim() })),
      }
      if (q.allow_freeform === false) out.allow_freeform = false
      if (q.multi_select) out.multi_select = true
      if (q.phase && q.phase !== 'reduction') out.phase = q.phase
      const inputType = q.inputType || 'choice'
      const input = {}
      if (inputType !== 'choice') input.type = inputType
      if (q.inputUnit) input.unit = q.inputUnit
      if (q.inputMin !== '' && q.inputMin != null && _num(q.inputMin) !== null) {
        input.min = _num(q.inputMin)
      }
      if (q.inputMax !== '' && q.inputMax != null && _num(q.inputMax) !== null) {
        input.max = _num(q.inputMax)
      }
      if (Object.keys(input).length) out.input = input
      return out
    })
}

/** newWeightingQuestion / weightingQuestionsToRows / rowsToWeightingQuestions —
 *  the v2 `weighting_questions` list. Separate slug namespace + shape from
 *  must_ask_questions: each maps its answer to metric weight deltas. */
export function newWeightingQuestion() {
  return { id: nextId(), slug: '', question: '', options: [], mapsTo: [], explanation: '' }
}

export function newMapsToRow() {
  return { id: nextId(), metric: '', delta: '' }
}

export function weightingQuestionsToRows(list) {
  return (list || []).map((wq) => ({
    id: nextId(),
    slug: wq.slug || '',
    question: wq.question || '',
    options: (wq.options || []).map((o) => ({ id: nextId(), label: o.label || '', value: o.value || '' })),
    mapsTo: Object.entries(wq.maps_to || {}).map(([metric, delta]) => ({ id: nextId(), metric, delta })),
    explanation: wq.explanation || '',
  }))
}

export function rowsToWeightingQuestions(rows) {
  return (rows || [])
    .filter((q) => q.question && q.question.trim())
    .map((q) => {
      const maps_to = {}
      for (const m of q.mapsTo || []) {
        if (!m.metric) continue
        maps_to[m.metric] = _num(m.delta) ?? 0
      }
      const out = {
        slug: (q.slug || '').trim(),
        question: q.question.trim(),
        maps_to,
        explanation: (q.explanation || '').trim(),
      }
      const opts = (q.options || [])
        .filter((o) => o.label && o.label.trim())
        .map((o) => ({ label: o.label.trim(), value: (o.value || o.label).trim() }))
      if (opts.length) out.options = opts
      return out
    })
}

/** weight_rules (v2, advanced): edited as a JSON textarea rather than a form —
 *  the shape (when/adjust/reason/bounds) is too free-form for rows. Pretty-print
 *  for editing; parse errors are RETURNED (not thrown) so the textarea can show
 *  them inline instead of crashing the editor. */
export function weightRulesToText(list) {
  if (!list || !list.length) return ''
  try {
    return JSON.stringify(list, null, 2)
  } catch {
    return ''
  }
}

export function textToWeightRules(text) {
  const trimmed = (text || '').trim()
  if (!trimmed) return { rules: [], error: null }
  let parsed
  try {
    parsed = JSON.parse(trimmed)
  } catch (e) {
    return { rules: [], error: `Invalid JSON: ${e.message}` }
  }
  if (!Array.isArray(parsed)) {
    return { rules: [], error: 'weight_rules must be a JSON array of rule objects.' }
  }
  return { rules: parsed, error: null }
}

/** Move item at index `i` by `delta` (±1); returns a new array. Out-of-range
 *  moves are a no-op (used by the up/down reorder buttons). */
export function moveItem(arr, i, delta) {
  const j = i + delta
  if (j < 0 || j >= arr.length) return arr
  const copy = arr.slice()
  ;[copy[i], copy[j]] = [copy[j], copy[i]]
  return copy
}

/** Light client-side validation mirroring the backend (instant UX). When
 *  `metricColumns` (a Set of valid columns) is provided, unknown metrics are
 *  flagged; otherwise metric membership is left to the backend. */
export function validateSpecClient(spec, metricColumns) {
  const errors = []
  const warnings = []
  const known = metricColumns instanceof Set ? metricColumns : null
  if (!spec.branch_label || !spec.branch_label.trim()) {
    errors.push('A branch label is required.')
  }
  if (!spec.intro || !String(spec.intro).trim()) {
    warnings.push('No intro text (v2) — customers see no application-profile summary.')
  }
  const weights = spec.scoring_weights || {}
  const wKeys = Object.keys(weights)
  if (!wKeys.length) errors.push('Add at least one scoring weight.')
  for (const [k, v] of Object.entries(weights)) {
    if (!isKnownMetric(known, k)) errors.push(`Weight "${k}" is not an AI-visible metric.`)
    if (typeof v !== 'number' || v < 0 || v > 1) errors.push(`Weight "${k}" must be between 0 and 1.`)
  }
  const sum = wKeys.reduce((s, k) => s + (Number(weights[k]) || 0), 0)
  if (wKeys.length && (sum < 0.95 || sum > 1.05)) {
    warnings.push(`Scoring weights sum to ${sum.toFixed(2)} (≈1.0 expected; they are re-normalized).`)
  }
  const hard = spec.hard_constraints || {}
  if (!Object.keys(hard).length) errors.push('Add at least one hard constraint.')
  for (const [k, c] of Object.entries(hard)) {
    if (isPackField(k)) {
      // Mirrors the backend: the ranker's constraint path cannot resolve a
      // pack figure, so such a gate would look set and never fire. Pack-level
      // gating belongs in system_requirements_map.
      errors.push(`Constraint "${k}" is a pack-level figure — gate it through system requirements.`)
      continue
    }
    if (Array.isArray(c)) continue
    if (c && typeof c === 'object' && 'not_in' in c) {
      const vals = c.not_in
      if (!Array.isArray(vals) || !vals.length || !vals.every((v) => typeof v === 'string' && v.trim())) {
        errors.push(`Constraint "${k}" not_in must be a non-empty list of non-empty strings.`)
      }
      continue
    }
    if (known && !known.has(k)) errors.push(`Constraint "${k}" is not an AI-visible metric.`)
  }
  for (const axis of ['plot_x', 'plot_y']) {
    if (spec[axis] && known && !known.has(spec[axis])) {
      errors.push(`${axis} "${spec[axis]}" is not an AI-visible metric.`)
    }
  }
  const slugs = (spec.must_ask_questions || []).map((q) => q.slug).filter(Boolean)
  const dupes = slugs.filter((s, i) => slugs.indexOf(s) !== i)
  if (dupes.length) errors.push(`Duplicate question slugs: ${[...new Set(dupes)].join(', ')}.`)

  // Per-question v2 fields: phase + input.type (both optional, both defaulted).
  for (const q of spec.must_ask_questions || []) {
    if (!q || typeof q !== 'object') continue
    const qSlug = q.slug || '<no slug>'
    if (q.phase && !QUESTION_PHASES.includes(q.phase)) {
      errors.push(`Question "${qSlug}" has an invalid phase "${q.phase}".`)
    }
    if (q.input && q.input.type && !INPUT_TYPES.includes(q.input.type)) {
      errors.push(`Question "${qSlug}" has an invalid input type "${q.input.type}".`)
    }
  }

  // weighting_questions (v2): own slug namespace; each needs maps_to + explanation.
  const weighting = spec.weighting_questions || []
  const wSlugs = []
  for (const wq of weighting) {
    if (!wq || typeof wq !== 'object') continue
    const wqSlug = wq.slug || '<no slug>'
    if (wq.slug) wSlugs.push(wq.slug)
    const mapsTo = wq.maps_to
    if (!mapsTo || typeof mapsTo !== 'object' || !Object.keys(mapsTo).length) {
      errors.push(`Weighting question "${wqSlug}" must define maps_to (metric → delta).`)
    } else if (known) {
      for (const mkey of Object.keys(mapsTo)) {
        if (!isKnownMetric(known, mkey)) {
          errors.push(`Weighting question "${wqSlug}" maps_to "${mkey}" is not an AI-visible metric.`)
        }
      }
    }
    if (!wq.explanation || !String(wq.explanation).trim()) {
      errors.push(`Weighting question "${wqSlug}" requires a non-empty explanation.`)
    }
  }
  const wDupes = wSlugs.filter((s, i) => wSlugs.indexOf(s) !== i)
  if (wDupes.length) {
    errors.push(`Duplicate weighting question slugs: ${[...new Set(wDupes)].join(', ')}.`)
  }
  const collisions = wSlugs.filter((s) => slugs.includes(s))
  if (collisions.length) {
    errors.push(
      `Weighting question slugs collide with must-ask questions: ${[...new Set(collisions)].join(', ')}.`,
    )
  }

  // system_requirements_map (v2): question_slug → allowlisted requirement field.
  const crossSlugs = (spec.cross_cutting_questions || []).map((q) => q.slug).filter(Boolean)
  const knownSlugs = new Set([...slugs, ...wSlugs, ...crossSlugs])
  const srMap = spec.system_requirements_map
  if (srMap && typeof srMap === 'object') {
    for (const [qSlug, field] of Object.entries(srMap)) {
      if (!knownSlugs.has(qSlug)) {
        errors.push(`system_requirements_map: "${qSlug}" does not reference an existing question slug.`)
      }
      if (!SYSTEM_REQUIREMENT_FIELDS.includes(field)) {
        errors.push(`system_requirements_map: "${field}" is not a known requirement field.`)
      }
    }
  }

  if (!(spec.disclaimers || []).length) {
    warnings.push('No disclaimers — proxy/data-gap branches must disclose limitations.')
  }
  return { errors, warnings }
}
